import { useState } from "react";
import { useSessionStore } from "../store/sessionStore";
import { downloadSession, uploadSession } from "../lib/session";
import { ShareModal } from "./ShareModal";
import { AttachKeysModal } from "./AttachKeysModal";
import { SyncModal } from "./SyncModal";
import { AttachDocsModal } from "./AttachDocsModal";
import { TemplatesModal } from "./TemplatesModal";
import { BackupModal } from "./BackupModal";
import { useBackupStore } from "../store/backupStore";
import { seedFromLastFrame } from "./DocumentPanel";

interface Props {
  view: "chat" | "library" | "wiki";
  onChangeView: (v: "chat" | "library" | "wiki") => void;
  onOpenKeys: () => void;
  /** Jump to a specific wiki page (used for in-context "what is this"
   *  links — e.g. Brainstorm's ?-button takes the user straight to
   *  the brainstorm wiki page instead of the wiki's welcome). */
  onOpenWikiPage: (id: string) => void;
}

export function SessionToolbar({ view, onChangeView, onOpenKeys, onOpenWikiPage }: Props) {
  const { addChannel, activeChannel, setToolsEnabled, setChannelMode, setBrainstormMoshPit, setChannelDocument, append } = useSessionStore();
  const active = activeChannel();
  const [shareOpen, setShareOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  const backup = useBackupStore();

  async function importChannel() {
    const s = await uploadSession();
    if (s) addChannel(s);
  }

  function exportChannel() {
    if (!active) return;
    downloadSession(active);
  }

  function toggleBrainstorm() {
    if (!active) return;
    const nextMode = active.mode === "brainstorm" ? "chat" : "brainstorm";
    // If the user has used the framing picker before, auto-seed with
    // their last pick — saves a click for repeat brainstorm starters.
    // Going through setChannelDocument means it lands in undo history,
    // so they can pop back to empty + show the picker again if they
    // want a different frame this time.
    if (nextMode === "brainstorm" && !(active.document ?? "").trim()) {
      const seed = seedFromLastFrame(active.title);
      if (seed) setChannelDocument(active.id, seed);
    }
    setChannelMode(active.id, nextMode);
    // Post a one-line system message into the thread so teammates
    // syncing the channel notice the mode change — Ask behavior
    // changes meaningfully between modes.
    append({
      role: "system",
      content: nextMode === "brainstorm"
        ? "Brainstorm enabled — Ask now updates the shared whiteboard on the right."
        : "Brainstorm disabled — Ask is back to normal chat.",
    });
  }

  return (
    <>
      <div className="titlebar">
        <h1>BabyAI Playground</h1>
        {view === "chat" && active && <span className="session-title">— {active.title}</span>}
        <span className="view-toggle">
          <button
            className={`view-toggle-btn ${view === "chat" ? "active" : ""}`}
            onClick={() => onChangeView("chat")}
          >
            Chat
          </button>
          <button
            className={`view-toggle-btn ${view === "library" ? "active" : ""}`}
            onClick={() => onChangeView("library")}
          >
            Library
          </button>
          <button
            className={`view-toggle-btn ${view === "wiki" ? "active" : ""}`}
            onClick={() => onChangeView("wiki")}
          >
            Wiki
          </button>
        </span>
        <span className="spacer" />
        {view === "chat" && (
          <>
            <button className="btn btn-ghost" onClick={importChannel}>Import</button>
            <button className="btn btn-ghost" onClick={exportChannel} disabled={!active}>
              Export
            </button>
            <button className="btn btn-ghost" onClick={() => setShareOpen(true)} disabled={!active}>
              Share
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => setAttachOpen(true)}
              disabled={!active}
              title="Attach encrypted keys to this channel"
            >
              {active?.encrypted_keys ? "Keys attached ✓" : "Attach keys"}
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => setSyncOpen(true)}
              disabled={!active}
              title="Sync this channel via GitHub"
            >
              {active?.sync_target ? "Synced ✓" : "Sync"}
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => active && setToolsEnabled(active.id, !active.tools_enabled)}
              disabled={!active}
              title="Toggle the agent loop (tools) for this channel. When on, the agent can fetch URLs, read GitHub files, and check the time."
            >
              {active?.tools_enabled ? "Tools on ✓" : "Tools off"}
            </button>
            <button
              className="btn btn-ghost"
              onClick={toggleBrainstorm}
              disabled={!active}
              title="Brainstorm mode: AI maintains a shared markdown whiteboard alongside the chat. Each Ask updates the whiteboard with the team's latest idea."
            >
              {active?.mode === "brainstorm" ? "Brainstorm ✓" : "Brainstorm"}
            </button>
            <button
              className="btn btn-ghost btn-icon"
              onClick={() => onOpenWikiPage("brainstorm")}
              title="What is Brainstorm mode? Opens the wiki page."
              aria-label="About Brainstorm mode"
            >
              ?
            </button>
            {active?.mode === "brainstorm" && (
              <button
                className="btn btn-ghost"
                onClick={() => setBrainstormMoshPit(active.id, !active.brainstorm_mp_enabled)}
                title="Mosh Pit: fire 3 parallel rewrites with different organizing strategies (by theme / by contributor / by maturity) and pick the best. Costs ~3x tokens per Ask — off by default."
              >
                {active.brainstorm_mp_enabled ? "MP ✓" : "MP off"}
              </button>
            )}
            <button
              className="btn btn-ghost"
              onClick={() => setDocsOpen(true)}
              disabled={!active}
              title="Attach library docs to this channel so the AI sees them as context on every turn."
            >
              {(active?.attached_documents?.length ?? 0) > 0
                ? `Docs (${active!.attached_documents!.length}) ✓`
                : "Docs"}
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => setTplOpen(true)}
              title="Save the current channel's shape as a reusable template, or spin up a new channel from a saved one."
            >
              Templates
            </button>
            {active && (
              <CostMeter session={active} />
            )}
          </>
        )}
        <button
          className="btn btn-ghost backup-chip"
          onClick={() => setBackupOpen(true)}
          title={
            !backup.token || !backup.repo
              ? "Backup not configured — click to set up GitHub durability"
              : backup.autoPush
                ? `Auto-backup on → ${backup.repo}`
                : `Backup configured but auto-push off → ${backup.repo}`
          }
        >
          <span className={`backup-dot ${backup.token && backup.repo ? (backup.autoPush ? "on" : "ready") : "off"}`} />
          Backup
        </button>
        <button className="btn" onClick={onOpenKeys}>Keys</button>
      </div>

      {shareOpen && active && (
        <ShareModal session={active} onClose={() => setShareOpen(false)} />
      )}
      {attachOpen && active && (
        <AttachKeysModal session={active} onClose={() => setAttachOpen(false)} />
      )}
      {syncOpen && active && (
        <SyncModal session={active} onClose={() => setSyncOpen(false)} />
      )}
      {docsOpen && active && (
        <AttachDocsModal session={active} onClose={() => setDocsOpen(false)} />
      )}
      {tplOpen && (
        <TemplatesModal current={active ?? undefined} onClose={() => setTplOpen(false)} />
      )}
      {backupOpen && (
        <BackupModal onClose={() => setBackupOpen(false)} />
      )}
    </>
  );
}

/** Compact token count: <1000 = bare, ≥1000 = k-suffix with one
 *  decimal, ≥1000000 = M-suffix. Reads at a glance in the toolbar. */
function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return (n / 1000).toFixed(n < 10_000 ? 1 : 0) + "k";
  return (n / 1_000_000).toFixed(1) + "M";
}

/** Rough byte → token estimate: 4 chars/token is the OpenAI-ish
 *  rule of thumb, close enough for "is this getting expensive"
 *  signal. Real tokenization varies by model; this isn't billing. */
function estimateTokensFromBytes(bytes: number): number {
  return Math.round(bytes / 4);
}

/** Cost meter — shows running total spent, plus a hover breakdown of
 *  what's driving the per-turn input bill (attached docs + brainstorm
 *  whiteboard, since those ride every Ask). Helps users notice when
 *  to detach a doc or compress the whiteboard. */
function CostMeter({ session }: { session: import("../lib/session").Session }) {
  const total = session.total_tokens;
  const docBytes = (session.attached_documents ?? []).reduce((s, d) => s + d.bytes, 0);
  const wbBytes = (session.document ?? "").length;
  const perTurnEstimate = estimateTokensFromBytes(docBytes + wbBytes);

  if (!total || total.turns === 0) {
    // Even with no AI dispatches yet, if there's per-turn baggage
    // worth knowing about, show the estimate so the user sees what
    // the next Ask will cost.
    if (perTurnEstimate < 100) return null;
    return (
      <span
        className="cost-meter"
        title={
          `Per-turn input estimate: ~${perTurnEstimate.toLocaleString()} tokens ` +
          `(attached docs ${(docBytes / 1024).toFixed(1)}KB + whiteboard ${(wbBytes / 1024).toFixed(1)}KB). ` +
          `Counts brainstorm-mode dispatches once one runs; chat-mode turns aren't counted yet.`
        }
      >
        ~{formatTokens(perTurnEstimate)} / turn
      </span>
    );
  }

  const tip =
    `${total.turns} AI turn${total.turns === 1 ? "" : "s"} · ` +
    `${total.prompt.toLocaleString()} prompt + ` +
    `${total.completion.toLocaleString()} completion tokens used.\n\n` +
    `Per-turn input baggage:\n` +
    `  attached docs: ${(docBytes / 1024).toFixed(1)}KB (~${estimateTokensFromBytes(docBytes).toLocaleString()} tok)\n` +
    `  whiteboard:    ${(wbBytes / 1024).toFixed(1)}KB (~${estimateTokensFromBytes(wbBytes).toLocaleString()} tok)\n` +
    `(Tracks brainstorm-mode dispatches; chat-mode turns aren't counted yet.)`;

  return (
    <span className="cost-meter" title={tip}>
      {formatTokens(total.prompt + total.completion)} tok
    </span>
  );
}
